import { createSelector } from '@reduxjs/toolkit'

import { ApplicationState } from 'redux/Stores/types'
import { FilterOption } from 'types/GlobalAttributeOptions'
import { IEntity, IEntityColumn, ISelectEntity } from 'types/GlobalEntities'
import {
  IMetric,
  isStandardMetric,
  MetricStatus,
  TMetricsResult
} from 'types/GlobalMetric'
import { TSlicesResult } from 'types/GlobalSlice'
import { sortAlphabeticalAsc } from 'utils/sortHelper'
import { selectEntitySlicesMapper } from 'redux/selectors/Slices'
import { selectEntityMetricsMapper } from 'redux/selectors/Metrics'

// numeric filter options are filtered with free input and carry no values
const isNumericFilterOption = (option: FilterOption | undefined) =>
  option?.type === 'NUMBER'

// value-list filter options are only usable when values exist
const hasFilterValues = (option: FilterOption | undefined) =>
  !!option?.values?.length

const emptySlicesResult: TSlicesResult = {
  all: [],
  disabled: [],
  available: [],
  custom: [],
  standard: [],
  selection_mode: 'multi_optional'
}

const emptyMetricsResult: TMetricsResult = {
  all: [],
  active: [],
  inactive: [],
  missing_data: []
}

const selectLoading = createSelector(
  [(state: ApplicationState) => state.EntityStore.fetchStatus !== 'success'],
  (loading) => loading
)

export const selectEntityData = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.EntityStore.data,
    selectLoading
  ],
  (
    entities,
    loading
  ): [
    {
      [entityId: string]: IEntity
    },
    boolean
  ] => [entities, loading]
)

export const selectEntities = createSelector(
  [
    // Input selectors:
    selectEntityData,
    (state: ApplicationState) => state.MetricStore.data,
    (state: ApplicationState) => selectEntitySlicesMapper(state),
    (state: ApplicationState, lang: 'sv' | 'en') =>
      selectEntityMetricsMapper(state, lang),
    (_: ApplicationState, lang: 'sv' | 'en') => lang
  ],
  (
    [entities, loading],
    metrics,
    slicesMapper,
    metricsMapper,
    lang
  ): [ISelectEntity[], boolean] => {
    const list = Object.values(entities)
      .sort((a, b) => sortAlphabeticalAsc(a.title[lang], b.title[lang]))
      .map((entity) => {
        const availableMetrics = getAvailableMetrics(entity, metrics)

        return {
          ...entity,
          columns: parseColumnsForOneEntity(entity, availableMetrics, lang),
          columnMapper: new Map(entity.columns.map((col) => [col.id, col])),
          filterOptions: {},
          slices: slicesMapper[entity.id] ?? emptySlicesResult,
          metrics: metricsMapper[entity.id] ?? emptyMetricsResult
        }
      })

    return [list, loading]
  }
)

const formatBaseColumn = (col: IEntityColumn) => ({
  ai_generated: col.ai_generated,
  entity: col.entity,
  id: col.id,
  index: col.index,
  title: col.name
})

const parseColumnsForOneEntity = (
  entity: IEntity,
  metrics: {
    [metricId: string]: IMetric
  },
  lang: 'sv' | 'en'
): ISelectEntity['columns'] => {
  return {
    locked: entity.columns
      .filter((col) => col.metrics.length > 0)
      .map((col) => ({
        ...formatBaseColumn(col),
        metrics: col.metrics
          .filter((metricId) => metrics[metricId])
          .map((metricId) => ({
            id: metricId,
            title: metrics[metricId].title
          }))
          .sort((a, b) => sortAlphabeticalAsc(a.title[lang], b.title[lang]))
      }))
      .sort((a, b) => a.index - b.index),
    options: entity.columns
      .filter((col) => col.hidden)
      .map((col) => formatBaseColumn(col))
      .sort((a, b) => a.index - b.index),
    standard: entity.columns
      .filter((col) => !col.hidden)
      .map((col) => formatBaseColumn(col))
      .sort((a, b) => a.index - b.index)
  }
}

/**
 * Get available metrics for entity
 */
const getAvailableMetrics = (
  entity: IEntity,
  metrics: {
    [id: string]: IMetric
  }
) => {
  const availableMetrics: { [metricId: string]: IMetric } = {}

  Object.values(metrics)
    .filter(
      (metric) =>
        isStandardMetric(metric) &&
        metric.entity_id === entity.id &&
        metric.status === MetricStatus.ACTIVE
    )
    .forEach((metric) => {
      availableMetrics[metric.id] = metric
    })

  return availableMetrics
}

export const selectAiColumnIds = createSelector(
  [
    (state: ApplicationState) => state.EntityStore.data,
    (_: ApplicationState, entityId: string | null) => entityId
  ],
  (data, entityId): Set<string> => {
    const columns = (entityId ? data[entityId] : undefined)?.columns ?? []

    return new Set(
      columns.filter((col) => col.ai_generated).map((col) => col.id)
    )
  }
)

export const selectOneEntity = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.EntityStore.data,
    (state: ApplicationState) => state.MetricStore.data,
    (state: ApplicationState) => state.FilterOptionStore.data,
    selectEntitySlicesMapper,
    (state: ApplicationState, _id: string, lang: 'sv' | 'en') =>
      selectEntityMetricsMapper(state, lang),
    (_: ApplicationState, id: string) => id,
    (_: ApplicationState, _id: string, lang: 'sv' | 'en') => lang
  ],
  (
    entities,
    metrics,
    filterOptions,
    slicesMapper,
    metricsMapper,
    id,
    lang
  ): ISelectEntity | null => {
    if (!entities[id]) {
      return null
    }

    const entity = entities[id]

    // get all metrics
    const availableMetrics = getAvailableMetrics(entity, metrics)

    // create a mapper for each column
    const columnMapper = new Map(entity.columns.map((col) => [col.id, col]))

    const allowedOptions: ISelectEntity['filterOptions'] = {}

    entity.columns
      .filter(
        (col) =>
          col.attribute_options.length > 0 &&
          // every attribute_option must be filterable to ensure all fact
          // tables have support of filtering by column.
          (col.attribute_options.every((attributeOptionId) =>
            isNumericFilterOption(filterOptions[attributeOptionId])
          ) ||
            col.attribute_options.every((attributeOptionId) =>
              hasFilterValues(filterOptions[attributeOptionId])
            ))
      )
      .forEach((col) => {
        const numeric = col.attribute_options.every((attributeOptionId) =>
          isNumericFilterOption(filterOptions[attributeOptionId])
        )

        const columnOptions = numeric
          ? []
          : Array.from(
            new Set(
              col.attribute_options.flatMap(
                (attributeOptionId) =>
                    filterOptions[attributeOptionId].values as string[]
              )
            )
          ).sort(sortAlphabeticalAsc)

        allowedOptions[col.id] = {
          id: col.id,
          name: col.name,
          numeric,
          options: columnOptions
        }
      })

    return {
      ...entity,
      columns: parseColumnsForOneEntity(entity, availableMetrics, lang),
      columnMapper,
      filterOptions: allowedOptions,
      slices: slicesMapper[id] ?? emptySlicesResult,
      metrics: metricsMapper[id] ?? emptyMetricsResult
    }
  }
)
